import React, { useState, useEffect, useCallback } from "react";
import {
  getMyPoints,
  getPointsLeaderboard,
} from "../../../services/pointsService";
import { getApiBaseUrl } from "../../../services/apiBase.js";
import {
  Award,
  Trophy,
  Zap,
  Activity,
  CheckCircle,
  RefreshCw,
} from "lucide-react";
import "./PointsWidget.css";

const PointsWidget = ({ pointsData: propPointsData }) => {
  const normalizePointsData = useCallback((data) => data?.data || data || null, []);

  const [pointsData, setPointsData] = useState(
    normalizePointsData(propPointsData)
  );
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(!propPointsData);
  const [error, setError] = useState(null);

  const [refreshing, setRefreshing] = useState(false);

  console.log("🎯 PointsWidget received props:", {
    pointsData: propPointsData,
  });

  const refreshPointsData = useCallback(async () => {
    try {
      setRefreshing(true);
      console.log("🔄 Manual refresh: Fetching points data...");

      // Fetch current user's points
      const data = await getMyPoints();
      console.log("Points data received:", data);

      setPointsData(data);

      // Also fetch leaderboard data
      try {
        const leaderboardData = await getPointsLeaderboard(5, 0);
        setLeaderboard(leaderboardData.leaderboard || []);
      } catch (leaderboardError) {
        console.warn("Could not fetch leaderboard:", leaderboardError);
        setLeaderboard([]);
      }

      setError(null);
    } catch (err) {
      console.error("❌ Error refreshing points:", err);
      setError(err.message || "Error refreshing points data");
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const normalizedPropPointsData = normalizePointsData(propPointsData);

    console.log(
      "🔄 PointsWidget useEffect triggered, propPointsData:",
      propPointsData
    );

    // Always update points data when props change (including from null to data)
    if (propPointsData !== undefined) {
      console.log("✅ Setting points data from props:", normalizedPropPointsData);
      setPointsData(normalizedPropPointsData);
      setLoading(false);
      setError(null);
    }

    // Only fetch fresh data if props are NOT provided (to avoid double-loading)
    let timer = null;
    if (propPointsData === undefined || propPointsData === null) {
      const fetchFreshPointsData = async () => {
        try {
          console.log("🔄 Fetching fresh points data from API...");
          const freshData = await getMyPoints();
          console.log("Fresh points data received:", freshData);
          const normalizedFreshData = normalizePointsData(freshData);
          if (normalizedFreshData && (normalizedFreshData.points || normalizedFreshData.total)) {
            setPointsData(normalizedFreshData);
            setError(null);
          }
        } catch (err) {
          console.warn("Could not fetch fresh points data:", err);
          // Don't set error here, use props data as fallback
        }
      };

      // Fetch fresh data after a short delay
      timer = setTimeout(fetchFreshPointsData, 1000);
    }

    // Always fetch leaderboard data
    const fetchLeaderboardData = async () => {
      try {
        console.log("🏆 Fetching leaderboard data...");
        const leaderboardData = await getPointsLeaderboard(5, 0);
        console.log("Leaderboard data received:", leaderboardData);
        setLeaderboard(leaderboardData.leaderboard || []);
      } catch (leaderboardError) {
        console.warn("Could not fetch leaderboard:", leaderboardError);
        setLeaderboard([]);
      }
    };

    // If props are explicitly undefined (not provided), fetch points data too
    if (propPointsData === undefined) {
      const fetchPointsData = async () => {
        try {
          setLoading(true);
          console.log("📊 Fetching points data...");

          // Fetch current user's points
          const data = await getMyPoints();
          console.log("Points data received:", data);

          setPointsData(normalizePointsData(data));
          setLoading(false);
        } catch (err) {
          console.error("❌ Error fetching points:", err);
          setError(err.message || "Error fetching points data");
          setLoading(false);
        }
      };

      fetchPointsData();
    }

    // Always fetch leaderboard
    fetchLeaderboardData();

    // Auto-refresh points data every 2 minutes (reduced frequency to avoid excessive reloads)
    const refreshInterval = setInterval(() => {
      console.log("🔄 Auto-refresh: Fetching latest points data...");
      refreshPointsData();
    }, 120000); // 2 minutes (120 seconds)

    // Return cleanup function for the timeout and interval
    return () => {
      if (timer) clearTimeout(timer);
      clearInterval(refreshInterval);
    };
  }, [normalizePointsData, propPointsData, refreshPointsData]); // Include refreshPointsData in dependencies

  if (loading) {
    return (
      <div className="points-widget loading">
        <div className="points-loader">Loading points data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="points-widget error">
        <p>Error loading points data: {error}</p>
      </div>
    );
  }

  // Handle the case where data isn't available yet
  if (!pointsData || (!pointsData.points && !pointsData.total)) {
    console.log("❌ PointsWidget: No points data condition triggered", {
      pointsData,
      hasData: pointsData?.data,
      hasPoints: pointsData?.points,
      hasTotal: pointsData?.total,
      propPointsData,
      pointsDataType: typeof pointsData,
      pointsKeys: pointsData ? Object.keys(pointsData) : "N/A",
    });
    // If we expect props but they're null/empty, show loading instead of "no data"
    if (propPointsData === null) {
      return (
        <div className="points-widget loading">
          <div className="points-loader">Loading points data...</div>
        </div>
      );
    }
    return (
      <div className="points-widget empty">
        <p>
          No points data available yet. Start using the platform to earn points!
        </p>
      </div>
    );
  }

  // Handle both old and new data structures
  const points = pointsData.points || pointsData;
  const userData = pointsData;

  // Provide fallback if points is missing
  const safePoints = points || {
    total: 0,
    quiz: 0,
    tutor: 0,
    summarizing: 0,
    curriculum: 0,
    progress: 0,
    feedback: 0,
    recommendation: 0,
    analytics: 0,
  };

  // Calculate user's position in leaderboard
  const userRank = leaderboard.find((user) => user.is_current_user)?.rank || 0;

  return (
    <div className="points-widget">
      <div className="points-header">
        <div className="points-total">
          <Trophy size={24} className="points-icon" />
          <div>
            <h3>{points.total || 0}</h3>
            <p>Total Points</p>
          </div>
        </div>

        <div className="points-actions">
          <button
            className="refresh-button"
            onClick={refreshPointsData}
            disabled={refreshing}
            title="Refresh points data"
          >
            <RefreshCw size={16} className={refreshing ? "spinning" : ""} />
          </button>

          {userRank > 0 && (
            <div className="points-rank">
              <Award size={24} className="points-icon" />
              <div>
                <h3>#{userRank}</h3>
                <p>Your Rank</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="points-breakdown">
        <h4>Points Breakdown</h4>
        <div className="points-categories">
          <div className="points-category">
            <Zap size={18} className="category-icon quiz" />
            <div className="category-name">Quiz</div>
            <div className="category-value">{points.quiz || 0}</div>
          </div>
          <div className="points-category">
            <Activity size={18} className="category-icon tutor" />
            <div className="category-name">Tutor</div>
            <div className="category-value">{points.tutor || 0}</div>
          </div>
          <div className="points-category">
            <CheckCircle size={18} className="category-icon summarizing" />
            <div className="category-name">Summary</div>
            <div className="category-value">{points.summarizing || 0}</div>
          </div>
        </div>
      </div>

      {leaderboard.length > 0 && (
        <div className="points-leaderboard">
          <h4>Leaderboard</h4>
          <ul className="leaderboard-list">
            {leaderboard.map((user, index) => (
              <li
                key={`${user.rank ?? index}-${user.name}`}
                className={user.is_current_user ? "current-user" : ""}
              >
                <span className="leaderboard-rank">#{user.rank ?? index + 1}</span>
                <span className="leaderboard-name">
                  {user.name || "Student"}
                  {user.is_current_user ? " (you)" : ""}
                </span>
                <span className="leaderboard-points">
                  {typeof user.points === "number" ? user.points : user.points?.total || 0}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PointsWidget;
